
import { Conversation } from '../../../hooks/useMessages'
import { useAuth } from '../../../hooks/useAuth'
import { decodeSharedContent } from '../../../components/base/SharedContentCard'

interface ConversationListProps {
  conversations: Conversation[]
  currentConversation: string | null
  onSelectConversation: (id: string) => void
  onNewConversation: () => void
  onNewGroup: () => void
  searchQuery: string
  onSearchChange: (query: string) => void
}

const ConversationList = ({
  conversations,
  currentConversation,
  onSelectConversation,
  searchQuery
}: ConversationListProps) => {
  const { user } = useAuth()

  const getOtherParticipant = (conversation: Conversation) => {
    return conversation.participants.find(p => p.user_id !== user?.id)
  }

  const getGroupMemberCount = (conversation: Conversation) => {
    return conversation.participants.length
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' })
    return date.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit' })
  }

  const getSenderName = (conversation: Conversation, senderId: string) => {
    if (senderId === user?.id) return 'You'
    const sender = conversation.participants.find(p => p.user_id === senderId)
    const name = sender?.user_profile?.username || 'Unknown'
    return name.split(' ')[0]
  }

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery.trim()) return true
    
    if (conv.is_group) {
      return conv.group_name?.toLowerCase().includes(searchQuery.toLowerCase())
    }
    
    const other = getOtherParticipant(conv)
    const name = other?.user_profile?.username || ''
    return name.toLowerCase().includes(searchQuery.toLowerCase())
  })

  return (
    <div className="flex-1 overflow-y-auto">
      {filteredConversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full px-6 text-center py-20">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-4">
            <i className="ri-message-3-line text-3xl text-purple-500"></i>
          </div>
          {searchQuery ? (
            <>
              <p className="text-sm font-medium text-gray-900">No chats found</p>
              <p className="text-xs text-gray-500 mt-1">Try a different search</p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-900">No conversations yet</p>
              <p className="text-xs text-gray-500 mt-1">Start chatting with someone</p>
            </>
          )}
        </div>
      ) : (
        filteredConversations.map((conversation) => {
          const isActive = currentConversation === conversation.id
          const unread = typeof conversation.unread_count === 'number' ? conversation.unread_count : 0
          const isGroup = conversation.is_group

          if (isGroup) {
            const memberCount = getGroupMemberCount(conversation)

            return (
              <div
                key={conversation.id}
                onClick={() => onSelectConversation(conversation.id)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all border-b border-gray-50 ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-l-purple-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                {/* Group Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white shadow-sm">
                    <i className={`${conversation.group_icon || 'ri-group-line'} text-xl`}></i>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-[15px] truncate ${unread > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'}`}>
                      {conversation.group_name || 'Group Chat'}
                    </p>
                    {conversation.last_message && (
                      <span className={`text-xs flex-shrink-0 ml-2 ${unread > 0 ? 'text-purple-600 font-medium' : 'text-gray-400'}`}>
                        {formatTime(conversation.last_message.created_at)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-sm truncate pr-2 ${unread > 0 ? 'text-gray-700' : 'text-gray-500'}`}>
                      {conversation.last_message
                        ? (() => {
                            const senderName = getSenderName(conversation, conversation.last_message.sender_id)
                            const decoded = decodeSharedContent(conversation.last_message.content)
                            if (decoded.isShared && decoded.content) {
                              const icon = decoded.content.type === 'drama' ? '🎬 ' : '📝 '
                              return `${senderName}: ${icon}Shared`
                            }
                            return `${senderName}: ${conversation.last_message.content}`
                          })()
                        : `${memberCount} participants`}
                    </p>
                    {unread > 0 && (
                      <span className="flex-shrink-0 min-w-[22px] h-[22px] px-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold rounded-full flex items-center justify-center shadow-sm">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          }

          // Direct message conversation
          const other = getOtherParticipant(conversation)

          return (
            <div
              key={conversation.id}
              onClick={() => onSelectConversation(conversation.id)}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all border-b border-gray-50 ${
                isActive
                  ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-l-purple-500'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 font-semibold text-lg overflow-hidden shadow-sm">
                  {other?.user_profile?.avatar_url ? (
                    <img src={other.user_profile.avatar_url} alt="" className="w-full h-full object-cover object-top" />
                  ) : (
                    other?.user_profile?.username?.[0]?.toUpperCase() || 'U'
                  )}
                </div>
                {/* Online indicator */}
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full"></div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className={`text-[15px] truncate ${unread > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'}`}>
                    {other?.user_profile?.username || 'Unknown User'}
                  </p>
                  {conversation.last_message && (
                    <span className={`text-xs flex-shrink-0 ml-2 ${unread > 0 ? 'text-purple-600 font-medium' : 'text-gray-400'}`}>
                      {formatTime(conversation.last_message.created_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    {conversation.last_message?.sender_id === user?.id && (
                      <i className="ri-check-double-line text-purple-500 text-sm flex-shrink-0"></i>
                    )}
                    <p className={`text-sm truncate ${unread > 0 ? 'text-gray-700' : 'text-gray-500'}`}>
                      {conversation.last_message
                        ? (() => {
                            const decoded = decodeSharedContent(conversation.last_message.content)
                            if (decoded.isShared && decoded.content) {
                              const icon = decoded.content.type === 'drama' ? '🎬 ' : '📝 '
                              return icon + 'Shared: ' + decoded.content.title
                            }
                            return conversation.last_message.content
                          })()
                        : 'No messages yet'}
                    </p>
                  </div>
                  {unread > 0 && (
                    <span className="flex-shrink-0 min-w-[22px] h-[22px] px-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold rounded-full flex items-center justify-center ml-2 shadow-sm">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

export default ConversationList
